import { FC } from "react";

import { Button, Form, Input, Select } from "antd";

import emailIcon from "data/icons/email.svg";
import phoneIcon from "data/icons/phone.svg";
import editIcon from "data/icons/edit.svg";
import { formatPhoneNumber } from "services/data.service";
import { Severity } from "store/cases/cases.types";
import { ICaseForOpen } from "services/interface.service";
import { Loading } from "components/common";
import { useCaseForm } from "helpers/useCaseForm";
import CaseEquipmentInfo from "./components/CaseEquipmentInfo";
import CaseContactNameField from "./components/CaseContactNameField";
import CaseCustomerCard from "./components/CaseCustomerCard";
import CaseSeveritiesField from "./components/CaseSeveritiesField";
import CaseSubContractorCard from "./components/CaseSubContractorCard";
import CaseCCField from "./components/CaseCCField";
import CaseFilesField from "./components/CaseFilesField";
import { useCaseFiles } from "./helpers/useCaseFiles";

import "./CaseForm.scss";

const initialValues: Partial<ICaseForOpen> = {
  subject: "",
  text: "",
  serial_number: "",
  severity: Severity.NORMAL,
  author_name: "",
  author_email: "",
  author_phone_number: "",
  contractorId: null,
}

interface IProps {
  afterEvent: () => void;
  serial_number?: string;
  id?: number;
  setContractorModalOpen: () => void;
}

const CaseForm: FC<IProps> = ({ afterEvent, serial_number = "", id, setContractorModalOpen }) => {
  const {
    files,
    setFiles,
    filesUploadStatus,
    onUploadFile,
    onRemoveFile,
    filesErrors,
    fileAttachments,
    setFileAttachments,
    setFilesUploadStatus,
  } = useCaseFiles();

  const {
    isMobile,
    form,
    handleCompleteForm,
    isCleared,
    onResetForm,
    findEquipment,
    caseEquipment,
    serialNumberError,
    isCustomerEdit,
    onCustomerSave,
    contractor,
    authorEmail,
    contractors,
    getContractors,
    onCustomerChange,
    debouncedClients,
    clients,
    handleContactCreate,
    onSubcontractorSave,
    subcontractor,
    isSubcontractorEdit,
    serialNumber,
    onSubcontractorChange,
    clientsCopy,
    setCustomerIsEdit,
    authorName,
    authorPhoneNumber,
    author,
    setAuthor,
    onSubcontractorEdit,
    getClientsCopy,
    isSubmitDisabled,
    isLoading,
    onContactEmailCheck,
    isAuthorEmailExist,
    setIsAuthorEmailExist,
  } = useCaseForm({
    serial_number,
    afterEvent,
    id,
    fileAttachments,
    filesUploadStatus,
    setFiles,
    setFileAttachments,
    setFilesUploadStatus,
  });

  return (
    <Form
      className={ `case-form-main ${ isMobile ? "mobile" : "" }` }
      name="case-form"
      layout="vertical"
      autoComplete="off"
      form={ form }
      onFinish={ handleCompleteForm }
      initialValues={ initialValues }
    >
      {
        // @ts-ignore
        (values, formInstance) => {
          return (
            <>
              { (serial_number && !isCleared) && (
                <div className="case-form-main-header">
                  The form is already partially filled.
                  <Button
                    type="link"
                    onClick={ onResetForm }
                  >
                    Reset
                  </Button>
                </div>
              ) }

              <h3>Complete the form</h3>

              <Form.Item
                label="Subject"
                name="subject"
                rules={ [ { required: true, message: "Please input subject!" } ] }
                style={ { marginBottom: 10 } }
              >
                <Input
                  className="with-count"
                  showCount
                  maxLength={ 150 }
                  placeholder="Add subject"
                />
              </Form.Item>

              <Form.Item
                label="Serial Number"
                name="serial_number"
                style={ { marginBottom: 10 } }
              >
                <Input
                  placeholder="Start with serial number or choose customer/subcontractor below"
                  onChange={ (e) => findEquipment(e.target.value) }
                />
              </Form.Item>

              { caseEquipment && (
                <CaseEquipmentInfo caseEquipment={ caseEquipment }/>
              ) }

              { (!caseEquipment && serialNumberError) && (
                <div className="notify-error">
                  { serialNumberError }
                </div>
              ) }

              <div className={ `case-form-group ${ isMobile ? "mobile" : "" }` }>
                <div className={ `user-info ${ isMobile ? "mobile" : "" }` }>
                  <div className="case-form-group-title">
                    <div>
                      Customer
                    </div>

                    { isCustomerEdit ? (
                      <Button
                        size="small"
                        onClick={ onCustomerSave }
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        type="text"
                        size="small"
                        icon={ <img src={ editIcon } alt="" style={ { margin: 0 } }/> }
                        onClick={ () => setCustomerIsEdit(true) }
                      />
                    ) }
                  </div>

                  { !isCustomerEdit && (
                    <CaseCustomerCard
                      contractor={ contractor }
                      authorName={ authorName }
                      authorPhoneNumber={ authorPhoneNumber }
                      authorEmail={ authorEmail }
                    />
                  ) }

                  <div
                    style={ isCustomerEdit
                      ? { visibility: "visible" }
                      : { visibility: "hidden", position: "absolute", top: -1000 } }
                  >
                    <Form.Item
                      label=""
                      name="customerValue"
                      style={ { marginBottom: 5 } }
                    >
                      <Select
                        disabled={ serialNumber && !serialNumberError }
                        options={ contractors }
                        showSearch
                        onSearch={ (value) => getContractors(value) }
                        filterOption={ false }
                        placeholder="Choose contractor"
                        notFoundContent={ null }
                        onChange={ onCustomerChange }
                      />
                    </Form.Item>

                    <div
                      style={ { display: "flex", justifySelf: "center" } }
                    >
                      <Button
                        type="link"
                        onClick={ setContractorModalOpen }
                      >
                        Create new one
                      </Button>
                    </div>

                    <CaseContactNameField
                      author={ author }
                      form={ form }
                      setAuthor={ setAuthor }
                      clients={ clients }
                      debouncedClients={ debouncedClients }
                      setIsAuthorEmailExist={ setIsAuthorEmailExist }
                    />

                    <Form.Item
                      className="mg-btn-0"
                      label="Email"
                      name="author_email"
                      rules={ [
                        { required: true, message: "Please input your email!" },
                        { type: "email", message: "Please input correct email!" },
                      ] }
                      style={ { marginBottom: 15 } }
                    >
                      <Input
                        placeholder="yourname@mail.com"
                        prefix={ <img src={ emailIcon } alt=""/> }
                        onChange={ async (e) => {
                          const authorEmailValue = e.target.value;

                          if (contractor && authorEmailValue) {
                            await onContactEmailCheck(contractor.value || contractor.id, authorEmailValue);
                          }
                        } }
                      />
                    </Form.Item>

                    <Form.Item
                      className="mg-btn-0"
                      label="Phone"
                      name="author_phone_number"
                      style={ { marginBottom: 0 } }
                    >
                      <Input
                        placeholder="+375 (00) 000-00-00"
                        prefix={ <img src={ phoneIcon } alt="" style={ { marginRight: "5px" } }/> }
                        onChange={ (e) => form.setFieldsValue({ author_phone_number: formatPhoneNumber(e.target.value) }) }
                      />
                    </Form.Item>

                    { (
                      contractor && authorEmail && authorName &&
                      !formInstance.getFieldError("author_name").length &&
                      !formInstance.getFieldError("author_email").length &&
                      !isAuthorEmailExist
                    ) && (
                      <div style={ { display: "flex", justifySelf: "center" } }>
                        <Button
                          type="link"
                          onClick={ handleContactCreate }
                        >
                          Add to Contact List
                        </Button>
                      </div>
                    ) }
                  </div>
                </div>

                <div className={ `user-info ${ isMobile ? "mobile" : "" }` }>
                  <div className="case-form-group-title">
                    <div>
                      Subcontractor
                    </div>

                    { isSubcontractorEdit ? (
                      <Button
                        size="small"
                        onClick={ onSubcontractorSave }
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        type="text"
                        size="small"
                        icon={ <img src={ editIcon } alt="" style={ { margin: 0 } }/> }
                        onClick={ onSubcontractorEdit }
                      />
                    ) }
                  </div>

                  { !isSubcontractorEdit && subcontractor && (
                    <CaseSubContractorCard subcontractor={ subcontractor }/>
                  ) }

                  <div
                    style={ isSubcontractorEdit
                      ? { visibility: "visible" }
                      : { visibility: "hidden", position: "absolute", top: -1000 } }
                  >
                    <Form.Item
                      label=""
                      name="subcontractor"
                      style={ { marginBottom: 5 } }
                    >
                      <Select
                        disabled={ serialNumber && !serialNumberError }
                        options={ contractors }
                        showSearch
                        onSearch={ (value) => getContractors(value) }
                        filterOption={ false }
                        placeholder="Choose contractor"
                        notFoundContent={ null }
                        onChange={ onSubcontractorChange }
                      />
                    </Form.Item>

                    <div
                      style={ { display: "flex", justifySelf: "center" } }
                    >
                      <Button
                        type="link"
                        onClick={ setContractorModalOpen }
                      >
                        Create new one
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <CaseCCField
                clientsCopy={ clientsCopy }
                getClientsCopy={ getClientsCopy }
              />

              <CaseFilesField
                files={ files }
                setFiles={ setFiles }
                filesUploadStatus={ filesUploadStatus }
                onUploadFile={ onUploadFile }
                onRemoveFile={ onRemoveFile }
                filesErrors={ filesErrors }
              />

              <CaseSeveritiesField form={ form }/>

              {/*{additionalEmails.length > 0 && (*/ }
              {/*	<Form.Item style={ { marginBottom: 0 } }>*/ }
              {/*		{additionalEmails.map((item: string, id: number) => (*/ }
              {/*			<Form.Item*/ }
              {/*				key={ id }*/ }
              {/*				className="mg-btn-0"*/ }
              {/*				label={ `Email ${ id + 2 }` }*/ }
              {/*				name={ `author_email-${ id + 2 }` }*/ }
              {/*				rules={ [ { type: "email", message: "Please input correct email!" } ] }*/ }
              {/*				style={ {*/ }
              {/*					display: "inline-block",*/ }
              {/*					width: "calc(50% - 10px)",*/ }
              {/*					marginRight: id % 2 === 0 ? "20px" : "0",*/ }
              {/*					marginTop: "15px",*/ }
              {/*				} }*/ }
              {/*			>*/ }
              {/*				<Input*/ }
              {/*					placeholder="yourname@mail.com"*/ }
              {/*					prefix={ <img src={ emailIcon } alt="" /> }*/ }
              {/*					value={ item }*/ }
              {/*					onChange={ (e) => {*/ }
              {/*						setAdditionalEmails(additionalEmails.map((mail, index) => index === id ? e.target.value : mail))*/ }
              {/*					} }*/ }
              {/*				/>*/ }
              {/*			</Form.Item>*/ }
              {/*		))}*/ }
              {/*	</Form.Item>*/ }
              {/*)}*/ }

              <Form.Item
                className="mg-btn-0"
                style={ { marginTop: "15px" } }
              >
                <Button
                  block
                  className="big-btn"
                  type="primary"
                  htmlType="submit"
                  disabled={ isSubmitDisabled }
                >
                  <Loading
                    isLoading={ isLoading }
                    height={ 25 }
                    width={ 81 }
                  >
                    Open Case
                  </Loading>
                </Button>
              </Form.Item>
            </>
          )
        } }
    </Form>
  )
}

export default CaseForm;
